import React from "react";
import { brand } from "../theme/brand";
import type { DemoProfileId } from "../demoProfiles";
import type { OuraConnection } from "../supabase/oura";
import type { ActivityBurnSource } from "../types/entries";
import { AccountSection } from "./sections/AccountSection";
import { MetabolismSection } from "./sections/MetabolismSection";
import { IntegrationsSection } from "./sections/IntegrationsSection";
import { DataPrivacySection } from "./sections/DataPrivacySection";
import { AccountTabSection } from "./sections/AccountTabSection";

export type Section = "profile" | "metabolism" | "integrations" | "data" | "account";

const NAV_ITEMS: { key: Section; label: string }[] = [
  { key: "profile",      label: "Profile" },
  { key: "metabolism",   label: "Metabolism" },
  { key: "integrations", label: "Apps & Devices" },
  { key: "data",         label: "Data" },
  { key: "account",      label: "Account" },
];

export function SettingsHeaderTabs({
  activeSection,
  onSelect,
}: {
  activeSection: Section;
  onSelect: (section: Section) => void;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Check initial scroll capacity once layout settles
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () =>
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    check();
    const raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Bring active tab into view by adjusting scrollLeft directly.
  // Never use scrollIntoView — it can scroll the document, not just the nav container.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const btn = el.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    if (!btn) return;
    const btnLeft = btn.offsetLeft;
    const btnRight = btnLeft + btn.offsetWidth;
    const viewLeft = el.scrollLeft;
    const viewRight = viewLeft + el.clientWidth;
    if (btnLeft < viewLeft + 48) {
      el.scrollLeft = Math.max(0, btnLeft - 48);
    } else if (btnRight > viewRight - 48) {
      el.scrollLeft = btnRight - el.clientWidth + 48;
    }
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, [activeSection]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setScrollLeft(el.scrollLeft);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      style={{ position: "relative", borderBottom: "1px solid rgba(148,163,184,0.18)" }}
    >
      {/* Left fade — visible only when scrolled > 0 */}
      {scrollLeft > 0 && (
        <div
          style={{
            position: "absolute",
            left: 18,
            top: 0,
            bottom: 0,
            width: 48,
            background: `linear-gradient(to right, ${brand.background.primary}, transparent)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {/* Right fade — only while there is more content to scroll to */}
      {canScrollRight && (
        <div
          style={{
            position: "absolute",
            right: 18,
            top: 0,
            bottom: 0,
            width: 48,
            background: `linear-gradient(to left, ${brand.background.primary}, transparent)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {/* Scrollable tab row */}
      <div
        ref={scrollRef}
        className="settings-tab-scroll"
        onScroll={handleScroll}
        style={{
          display: "flex",
          flexWrap: "nowrap",
          overflowX: "auto",
          paddingLeft: 18,
          paddingRight: 18,
          overscrollBehaviorX: "contain",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        } as React.CSSProperties}
      >
        {NAV_ITEMS.map((item) => {
          const active = activeSection === item.key;
          return (
            <button
              key={item.key}
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(item.key)}
              style={{
                flex: "0 0 auto",
                border: "none",
                borderBottom: active
                  ? `2px solid ${brand.accent.primary}`
                  : "2px solid transparent",
                borderRadius: 0,
                background: "transparent",
                color: active ? "#ffffff" : "rgba(255,255,255,0.45)",
                padding: "0.58rem 14px 0.5rem",
                fontSize: "0.77rem",
                fontWeight: active ? 700 : 500,
                lineHeight: 1.1,
                cursor: "pointer",
                whiteSpace: "nowrap",
                minHeight: 40,
                transition: "color 110ms ease",
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export type SettingsPageProps = {
  vitals: any;
  activitySummary: { label: string; factor: number };
  settingsDataStatusMsg: string | null;
  todayYmd: string;
  entriesAsc: any[];
  openVitalsEditor: () => void;
  openCsvImportModal: () => void;
  downloadTextFile: (filename: string, data: string) => void;
  entriesToCsv: (entries: any[]) => string;
  exportProfileJson: () => void;
  importProfileFromSettings: (file: File) => void;
  resetAll: () => void;
  fmtHeightFtIn: (heightIn: number) => string;
  calcAge: (birthday: string) => number | null;
  supabaseUserId: string | null;
  supabaseUserEmail: string | null;
  supabaseStatus: string;
  supabaseLastSyncAt: string | null;
  supabaseError: string | null;
  onSupabaseSyncNow: () => void;
  onSupabaseSignOut: () => void;
  onSupabaseSignIn: () => void;
  isDemoMode: boolean;
  demoProfileId: DemoProfileId;
  onStartDemo: (id: DemoProfileId) => void;
  displayName: string;
  avatarKey: string;
  onSaveDisplayNameAndAvatar: (name: string, key: string) => Promise<void>;
  // Oura Ring integration (all optional — backward-compatible)
  ouraConnection?: OuraConnection | null;
  ouraStatus?: "idle" | "syncing" | "error";
  ouraRedirectError?: string | null;
  onOuraConnect?: () => Promise<void>;
  onOuraDisconnect?: () => Promise<void>;
  onOuraSyncNow?: () => Promise<void>;
  onOuraToggleAuthoritative?: (value: boolean) => Promise<void>;
  // TODO: wire to a dedicated backend auto_sync column when available.
  onOuraToggleAutoSync?: (value: boolean) => Promise<void>;
  // Activity burn source (shared Oura + Apple Health control)
  activityBurnSource?: ActivityBurnSource;
  onActivityBurnSourceChange?: (val: ActivityBurnSource) => Promise<void>;
  // Apple Health integration (all optional — native iOS only)
  healthKitEnabled?: boolean;
  onHealthKitToggle?: (val: boolean) => Promise<void>;
  healthKitStatus?: "idle" | "syncing" | "error";
  healthKitLastSyncAt?: string | null;
  onHealthKitSyncNow?: () => Promise<void>;
  onNavigateToLogbook?: () => void;
  initialSection?: Section;
  activeSection?: Section;
  onActiveSectionChange?: (section: Section) => void;
};

const navItemStyle = (active: boolean): React.CSSProperties => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  border: "none",
  borderLeft: active ? `3px solid ${brand.accent.primary}` : "3px solid transparent",
  borderRadius: "0 6px 6px 0",
  background: active ? "rgba(59,130,246,0.1)" : "transparent",
  color: active ? "#f8fafc" : "rgba(229,231,235,0.72)",
  padding: "10px 14px",
  fontSize: "0.9rem",
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
  transition: "background 110ms ease, color 110ms ease",
  letterSpacing: "-0.005em",
});

export function SettingsPage(props: SettingsPageProps) {
  const {
    vitals,
    activitySummary,
    settingsDataStatusMsg,
    todayYmd,
    entriesAsc,
    openVitalsEditor,
    openCsvImportModal,
    downloadTextFile,
    entriesToCsv,
    exportProfileJson,
    importProfileFromSettings,
    resetAll,
    fmtHeightFtIn,
    calcAge,
    supabaseUserId,
    supabaseUserEmail,
    supabaseStatus,
    supabaseLastSyncAt,
    supabaseError,
    onSupabaseSyncNow,
    onSupabaseSignOut,
    onSupabaseSignIn,
    isDemoMode,
    demoProfileId,
    onStartDemo,
    displayName,
    avatarKey,
    onSaveDisplayNameAndAvatar,
    ouraConnection,
    ouraStatus,
    ouraRedirectError,
    onOuraConnect,
    onOuraDisconnect,
    onOuraSyncNow,
    onOuraToggleAuthoritative,
    onOuraToggleAutoSync,
    activityBurnSource,
    onActivityBurnSourceChange,
    healthKitEnabled,
    onHealthKitToggle,
    healthKitStatus,
    healthKitLastSyncAt,
    onHealthKitSyncNow,
    onNavigateToLogbook,
    initialSection,
    activeSection: controlledActiveSection,
    onActiveSectionChange,
  } = props;

  const [internalActiveSection, setInternalActiveSection] = React.useState<Section>(initialSection ?? "profile");
  const activeSection = controlledActiveSection ?? internalActiveSection;
  const setActiveSection = React.useCallback((section: Section) => {
    if (controlledActiveSection === undefined) setInternalActiveSection(section);
    onActiveSectionChange?.(section);
  }, [controlledActiveSection, onActiveSectionChange]);
  React.useEffect(() => {
    if (controlledActiveSection !== undefined) return;
    if (!initialSection) return;
    setInternalActiveSection(initialSection);
  }, [initialSection, controlledActiveSection]);

  const latestLoggedWeight = React.useMemo(() => {
    for (let i = entriesAsc.length - 1; i >= 0; i -= 1) {
      const candidate = entriesAsc[i];
      if (candidate?.weight != null && Number.isFinite(candidate.weight)) {
        return Number(candidate.weight);
      }
    }
    return null;
  }, [entriesAsc]);
  const metabolismWeightValue = latestLoggedWeight ?? (Number.isFinite(vitals.currentWeightLbs) ? Number(vitals.currentWeightLbs) : null);
  const metabolismWeightSourceLabel = latestLoggedWeight !== null ? "Last logged" : null;

  const renderSection = () => {
    switch (activeSection) {
      case "profile":
        return (
          <AccountSection
            displayName={displayName}
            avatarKey={avatarKey}
            onSaveDisplayNameAndAvatar={onSaveDisplayNameAndAvatar}
            supabaseUserId={supabaseUserId}
            supabaseUserEmail={supabaseUserEmail}
            supabaseStatus={supabaseStatus}
            supabaseError={supabaseError}
            isDemoMode={isDemoMode}
          />
        );
      case "metabolism":
        return (
          <MetabolismSection
            vitals={vitals}
            activitySummary={activitySummary}
            openVitalsEditor={openVitalsEditor}
            fmtHeightFtIn={fmtHeightFtIn}
            calcAge={calcAge}
            isDemoMode={isDemoMode}
            displayedWeightLbs={metabolismWeightValue}
            displayedWeightSourceLabel={metabolismWeightSourceLabel}
          />
        );
      case "integrations":
        return (
          <IntegrationsSection
            ouraConnection={ouraConnection}
            ouraStatus={ouraStatus}
            ouraRedirectError={ouraRedirectError}
            onOuraConnect={onOuraConnect}
            onOuraDisconnect={onOuraDisconnect}
            onOuraSyncNow={onOuraSyncNow}
            onOuraToggleAuthoritative={onOuraToggleAuthoritative}
            onOuraToggleAutoSync={onOuraToggleAutoSync}
            supabaseUserId={supabaseUserId}
            isDemoMode={isDemoMode}
            activityBurnSource={activityBurnSource}
            onActivityBurnSourceChange={onActivityBurnSourceChange}
            healthKitEnabled={healthKitEnabled}
            onHealthKitToggle={onHealthKitToggle}
            healthKitStatus={healthKitStatus}
            healthKitLastSyncAt={healthKitLastSyncAt}
            onHealthKitSyncNow={onHealthKitSyncNow}
          />
        );
      case "data":
        return (
          <DataPrivacySection
            todayYmd={todayYmd}
            entriesAsc={entriesAsc}
            openCsvImportModal={openCsvImportModal}
            downloadTextFile={downloadTextFile}
            entriesToCsv={entriesToCsv}
            exportProfileJson={exportProfileJson}
            importProfileFromSettings={importProfileFromSettings}
            isDemoMode={isDemoMode}
            onNavigateToLogbook={onNavigateToLogbook}
          />
        );
      case "account":
        return (
          <AccountTabSection
            supabaseUserId={supabaseUserId}
            supabaseUserEmail={supabaseUserEmail}
            supabaseStatus={supabaseStatus}
            supabaseLastSyncAt={supabaseLastSyncAt}
            supabaseError={supabaseError}
            onSupabaseSyncNow={onSupabaseSyncNow}
            onSupabaseSignIn={onSupabaseSignIn}
            onSupabaseSignOut={onSupabaseSignOut}
            isDemoMode={isDemoMode}
            isAuthenticated={!!supabaseUserId}
            resetAll={resetAll}
          />
        );
    }
  };

  return (
    <>
      <style>{`
        .settings-tab-scroll::-webkit-scrollbar {
          display: none;
        }
        .settings-shell {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 0;
          min-height: 100%;
          align-items: start;
          min-width: 0;
        }
        .settings-nav {
          border-right: 1px solid rgba(255,255,255,0.07);
          padding: 0 0 2rem;
          position: sticky;
          top: 18px;
        }
        .settings-mobile-tablist {
          display: none;
        }
        .settings-content {
          padding: 0 0 2rem 2rem;
          min-width: 0;
          max-width: 800px;
          width: 100%;
          display: grid;
          gap: 2rem;
          align-content: start;
        }
        .settings-content > * {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .settings-nav-item:hover {
          background: rgba(59,130,246,0.07) !important;
          color: rgba(255,255,255,0.9) !important;
        }
        @media (max-width: 640px) {
          .settings-shell {
            grid-template-columns: 1fr;
          }
          .settings-mobile-frame {
            width: 100%;
            max-width: 420px;
            margin: 0 auto;
            min-width: 0;
          }
          .settings-nav {
            display: none;
          }
          .settings-content {
            padding: 0.1rem 0 2rem;
            max-width: 100%;
            gap: 1.2rem;
          }
        }
      `}</style>

      <div className="settings-mobile-frame">
      <div className="settings-shell" style={{ width: "100%" }}>
        {/* Sub-navigation */}
        <nav className="settings-nav" aria-label="Settings sections">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              className="settings-nav-item"
              style={navItemStyle(activeSection === item.key)}
              onClick={() => setActiveSection(item.key)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Content panel */}
        <main className="settings-content">
          {renderSection()}

          {settingsDataStatusMsg && (
            <div
              style={{
                borderRadius: 10,
                padding: "0.6rem 0.8rem",
                background: (settingsDataStatusMsg.startsWith("Imported") || settingsDataStatusMsg.startsWith("Exported"))
                  ? "rgba(34,197,94,0.07)"
                  : "rgba(239,68,68,0.07)",
                border: `1px solid ${(settingsDataStatusMsg.startsWith("Imported") || settingsDataStatusMsg.startsWith("Exported"))
                  ? "rgba(34,197,94,0.28)"
                  : "rgba(239,68,68,0.28)"}`,
                color: (settingsDataStatusMsg.startsWith("Imported") || settingsDataStatusMsg.startsWith("Exported"))
                  ? brand.state.success
                  : "#fca5a5",
                fontSize: "0.87rem",
              }}
            >
              {settingsDataStatusMsg}
            </div>
          )}
        </main>
      </div>
      </div>
    </>
  );
}
